import { Modal, Button, message } from 'antd';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { CloseActionBtn } from '../../lib/Resources';
import { storageService } from '../../lib/db/StorageService';
import { middleEllipsis, orderObject, removeEmpty } from '../../lib/Utils';
import { UserAccount } from '../../lib/models/UserAccount';
import { allAdminDataState, connectionAccountState } from '../../recoil/atoms';
import UserAvatar from './UserAvatar';
import { Web3Service } from '../../lib/web3/Web3Service';

export const AdminConfirmationModal = ({
  visible,
  onCancel,
  onSuccess,
  user,
  field,
  title,
  requestBody,
  requestAction,
}: {
  visible: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  user?: UserAccount;
  field?: string;
  requestBody?: Record<string, string>;
  requestAction?: null | string;
  title: string;
}) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const currentAccount = useRecoilValue(connectionAccountState);
  const [adminData, setAdminData] = useRecoilState(allAdminDataState);

  const actions = {
    verified: {
      true: 'unverify',
      false: 'verify',
    },
    banned: {
      true: 'unban',
      false: 'ban',
    },
    notAllowedToMint: {
      true: 'enable-minting',
      false: 'disable-minting',
    },
    canCreateDrop: {
      false: 'enable-drop',
      true: 'disable-drop',
    },
    leaderboard: {
      false: 'enable-leaderboard',
      true: 'disable-leaderboard',
    },
  };

  useEffect(() => {
    setSubmitting(false);
  }, [user]);

  const handleResponse = (response) => {
    if (response.user) {
      setAdminData({
        ...adminData,
        users: adminData.users.map((userData) => {
          if (userData.id === response.user?.id) {
            return {
              ...userData,
              ...response.user,
            };
          }
          return userData;
        }),
      });
    }

    if (response.message) {
      message.success(response.message);
      onCancel();
      onSuccess?.();
    }

    if (response.error) {
      message.error(response.error);
    }

    setSubmitting(false);
  };

  const onUpdateAccountType = async (payload = {}, signature) => {
    const response = await storageService.updateAccountType({
      walletAddress: user.walletAddress,
      payload: { ...payload, signature },
    });

    handleResponse(response);
  };

  async function onSubmit() {
    setSubmitting(true);

    const action = actions[field]?.[user[field]] || requestAction;
    const timestamp = dayjs().format();

    const payload = {
      timestamp,
      walletAddress: currentAccount.address,
      action,
      ...requestBody,
    };

    const { connection } = window;
    const web3Service = new Web3Service(connection);

    const signData = JSON.stringify(
      orderObject(
        removeEmpty({
          ...payload,
        }),
      ),
    );

    const { signature } = await web3Service.signPersonalMessage(
      signData,
      currentAccount.address,
    );

    if (!signature) {
      message.warn(t('Error occurred while getting the signature'), 5);
      setSubmitting(false);
      return;
    }

    if (field === 'accountType') {
      await onUpdateAccountType(payload, signature);
      return;
    }
    const response = await storageService.submitCurationUpdate({
      action,
      walletAddress: user.walletAddress,
      payload: {
        ...payload,
        signature,
      },
    });

    handleResponse(response);
  }

  return (
    <Modal
      title=""
      visible={visible}
      centered
      onCancel={onCancel}
      footer={null}
      className="admin-confirmation-filter-modal"
      closeIcon={
        <div>
          <img src={CloseActionBtn} alt="Close action" height={20} width={20} />
        </div>
      }
    >
      <div className="admin-confirmation-title">{title}</div>
      <div className="admin-confirmation-container">
        <UserAvatar
          user={user}
          IdenticonSize={40}
          className="user-avatar-container h-30 relative"
        />
        <div style={{ marginTop: '1rem' }}>
          {middleEllipsis(user?.walletAddress, 4)}
        </div>
      </div>
      <div className="admin-confirmation-bottom">
        <Button
          type="primary"
          className="nft-card-buy-button"
          loading={submitting}
          onClick={onSubmit}
        >
          {t('Confirm')}
        </Button>
      </div>
    </Modal>
  );
};
